import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileDown, ShieldCheck, History } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "./hrUtils";

const ACTIONS = { create: "Création", update: "Modification", delete: "Suppression", upload: "Ajout document", download: "Consultation document", export: "Export", export_rgpd: "Export RGPD", anonymize: "Anonymisation" };

export const HrExports = ({ ename }) => {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const canRgpd = ["owner", "hr"].includes(user?.role);
  const [year, setYear] = useState(new Date().getFullYear());
  const [audit, setAudit] = useState([]);
  const [ret, setRet] = useState(null);

  useEffect(() => {
    if (isManager) return;
    api.get("/hr/audit").then((r) => setAudit(r.data)).catch(() => {});
    if (canRgpd) api.get("/hr/rgpd/retention").then((r) => setRet(r.data)).catch(() => {});
  }, [isManager, canRgpd]);

  const dl = (kind, fmt) => {
    const q = kind === "conges" && year ? `?year=${year}` : "";
    downloadBlob(`/hr/exports/${kind}.${fmt}${q}`, `${kind}${kind === "conges" ? `-${year}` : ""}.${fmt}`).catch((e) => toast.error(apiErr(e)));
  };
  if (isManager) return <div className={`${CARD} p-6 text-sm text-gray-400`} data-testid="rh-exports">Les exports et le journal RH sont réservés au propriétaire, à l'administrateur et au rôle RH.</div>;

  return (
    <div className="space-y-4" data-testid="rh-exports">
      <div className={`${CARD} p-4 sm:p-6`}>
        <Label className="mb-3">Exports</Label>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-2"><p className="font-semibold">Congés</p><Input data-testid="rh-export-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className="h-8 w-24 rounded-xl" /></div>
            <div className="mt-3 flex gap-2">
              <Button data-testid="rh-export-conges-csv" variant="outline" onClick={() => dl("conges", "csv")} className="rounded-full"><FileSpreadsheet className="mr-1 h-4 w-4" />CSV</Button>
              <Button data-testid="rh-export-conges-pdf" variant="outline" onClick={() => dl("conges", "pdf")} className="rounded-full"><FileDown className="mr-1 h-4 w-4" />PDF</Button>
            </div>
          </div>
          <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-500">Le registre du personnel se trouve dans l'onglet « Registre du personnel ».</div>
        </div>
      </div>
      {ret && (
        <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-retention">
          <Label className="mb-2 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> RGPD — conservation ({ret.retention_years} ans après la sortie)</Label>
          {ret.items.length === 0 ? <p className="text-sm text-gray-400">Aucun salarié sorti à suivre.</p> : ret.items.map((i) => (
            <div key={i.employee_id} className="flex items-center justify-between py-1.5 text-sm">
              <span>{i.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${i.due ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-500"}`}>{i.purge_date ? `${i.due ? "À anonymiser depuis le" : "Purge le"} ${fmtDate(i.purge_date)}` : "Date de sortie manquante"}</span>
            </div>
          ))}
        </div>
      )}
      <div className={`${CARD} p-4 sm:p-6`} data-testid="rh-audit">
        <Label className="mb-2 flex items-center gap-1.5"><History className="h-4 w-4" /> Journal RH (accès et modifications)</Label>
        {audit.length === 0 ? <p className="text-sm text-gray-400">Aucune entrée.</p> : (
          <div className="max-h-96 divide-y divide-gray-50 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-x-3 py-2 text-xs">
                <span className="text-gray-400">{fmtDate(a.at)} {String(a.at).slice(11, 16)}</span>
                <span className="font-semibold">{a.user_name || a.user}</span>
                <span>{ACTIONS[a.action] || a.action} · {a.data_type}</span>
                {a.employee_id && <span className="text-gray-500">{ename(a.employee_id)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
